/**
 * Dataset Filter Enums — FE-side enum value definitions for known fields.
 *
 * The backend `fields` API returns `enumValues` only for EF (Employee Field)
 * custom fields (sourced from CompanySetting.enums). For model-level keyword
 * fields (e.g., PSLF form_status, SYF enrollment_state), the backend does NOT
 * populate enum values.
 *
 * This registry provides FE-side enum values for known fields so that the
 * DatasetFilters component can render dropdowns instead of free-text inputs.
 *
 * Backend-provided enum values always take precedence — this is a fallback.
 */

/**
 * Enum values keyed by the camelCase field name as returned by the fields API.
 * Each dataset maps field name → array of known values.
 */
type DatasetEnumMap = Record<string, string[]>;

/**
 * Known enum values per dataset.
 *
 * Field names use camelCase to match the backend fields API response.
 * The `name` value in the API response is the OpenSearch field name camelized
 * (e.g., "companyPartnerTypeKeyword"). The mapping fallback path also returns
 * camelCase keys (via snakeToCamelCase conversion).
 */
const DATASET_ENUM_REGISTRY: Record<string, DatasetEnumMap> = {
  /**
   * PSLF Forms — PslfForm.search_data field names (camelCase, matching fields API).
   * AASM states from app/models/pslf_form.rb lines 149–163.
   */
  'pslf-forms': {
    formStatus: [
      'borrower_signed',
      'employer_signed',
      'awaiting_submit',
      'submit_pending',
      'submit_errored',
      'submitted',
      're_submit_errored',
      're_submit_pending',
      're_submitted',
      'rejected',
      'cancelled',
      'expired',
      'abandoned',
    ],
    formType: ['currentEmployer', 'priorEmployer'],
    formFaxVerified: ['not_verified', 'verified', 'human_verified', 'too_old_to_verify'],
  },

  /**
   * SYF Match Participants — MatchParticipant search_data field names.
   * AASM states from app/models/match_participant.rb.
   * Values are stored with the SYF.* prefix in the DB.
   */
  'syf-match-participants': {
    syfMatchParticipantEnrollmentStateKeyword: [
      'SYF.EnrollmentState.NOT_STARTED',
      'SYF.EnrollmentState.DRAFT',
      'SYF.EnrollmentState.MISSING_INFORMATION',
      'SYF.EnrollmentState.COMPLETE',
    ],
    syfMatchParticipantStateKeyword: [
      'SYF.ParticipantState.CREATED',
      'SYF.ParticipantState.ACTIVE',
      'SYF.ParticipantState.INACTIVE',
      'SYF.ParticipantState.HELD',
    ],
  },

  /**
   * SLR Contribution Plan Participants — search_data field names.
   * From app/models/concerns/indices/slr/contribution_plan_participant_builder.rb.
   */
  'slr-contribution-plan-participants': {
    slrContributionPlanParticipantEnrollmentStatusKeyword: [
      'participating',
      'pending',
      'deactivated',
    ],
    slrContributionPlanParticipantPslfEnrollmentStatusKeyword: ['Active', 'None'],
    slrContributionPlanParticipantSyfEnrollmentStateKeyword: [
      'SYF.EnrollmentState.NOT_STARTED',
      'SYF.EnrollmentState.DRAFT',
      'SYF.EnrollmentState.MISSING_INFORMATION',
      'SYF.EnrollmentState.COMPLETE',
      'None',
    ],
  },

  /**
   * Companies — Company search_data field names (camelCase, matching fields API).
   * From app/models/concerns/indices/company/company_builder.rb.
   */
  companies: {
    companyPartnerTypeKeyword: ['PRIVATE', 'PSLF_ELIGIBLE'],
    companyTypeKeyword: ['trial', 'live', 'demo'],
    companyProductsEnabledKeyword: ['TA', 'SLR', 'PSLF', 'SYF'],
    companyTasProgramTemplateTypesKeyword: [
      'TAS.ProgramType.TUITION',
      'TAS.ProgramType.STUDENT_LOAN_REPAYMENT',
      'TAS.ProgramType.PROFESSIONAL_DEVELOPMENT',
      'TAS.ProgramType.TRAINING_CERTIFICATION',
      'TAS.ProgramType.OTHER',
    ],
    companySsoProviderKeyword: ['NONE', 'OKTA', 'AZURE_AD', 'ONELOGIN', 'GOOGLE', 'SAML'],
    companyPaymentMethodKeyword: ['ACH', 'CHECK', 'WIRE'],
    companyPaymentFrequencyKeyword: ['MONTHLY', 'SEMI_MONTHLY', 'BI_WEEKLY', 'WEEKLY'],
  },
};

/**
 * Get FE-defined enum values for a specific field in a dataset.
 *
 * @param dataset - The dataset key (e.g., 'pslf-forms', 'syf-match-participants')
 * @param fieldKey - The camelCase field key
 * @returns Array of enum values, or undefined if not defined
 */
export function getFieldEnumValues(dataset: string, fieldKey: string): string[] | undefined {
  return DATASET_ENUM_REGISTRY[dataset]?.[fieldKey];
}

/**
 * Get all FE-defined enum mappings for a dataset.
 *
 * @param dataset - The dataset key
 * @returns Map of field key → enum values, or empty object
 */
export function getDatasetEnums(dataset: string): DatasetEnumMap {
  return DATASET_ENUM_REGISTRY[dataset] ?? {};
}

/**
 * Apply FE-defined enum values to fields that don't already have backend-provided enums.
 * Backend values always take precedence.
 *
 * @param fields - Array of field options to enrich
 * @param dataset - The current dataset key
 * @returns The same array with enum values applied where applicable
 */
export function applyFallbackEnumValues<T extends { key: string; enumValues?: string[] }>(
  fields: T[],
  dataset: string
): T[] {
  const datasetEnums = getDatasetEnums(dataset);

  if (Object.keys(datasetEnums).length === 0) {
    return fields;
  }

  return fields.map((field) => {
    // Backend-provided enum values take precedence
    if (field.enumValues && field.enumValues.length > 0) {
      return field;
    }

    const fallbackValues = datasetEnums[field.key];
    if (fallbackValues) {
      return { ...field, enumValues: fallbackValues };
    }

    return field;
  });
}
